import { DetectionMethod, LanguageStatus } from '@/constants/enum';
import { ILanguageList } from '@/types/language';

export const DETECTION_LABELS: Record<DetectionMethod, string> = {
  [DetectionMethod.ShopLocale]: 'Shop locale',
  [DetectionMethod.ShopifyMarkets]: 'Shopify Markets',
  [DetectionMethod.Ip]: 'Visitor IP / geolocation',
  [DetectionMethod.Url]: 'URL parameter',
  [DetectionMethod.Browser]: 'Browser language',
};

// RTL languages — the editor target inputs should render RTL for these.
export const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur', 'ps', 'ckb', 'sd', 'ug', 'yi', 'ks'];

// Section render order + label. Fields are rendered data-driven from the API, but use this
// map for human labels; any key missing here falls back to a humanized key.
export const SECTION_LABELS: Record<string, string> = {
  trackingStatus: 'Tracking status',
  trackingForm: 'Tracking form',
  trackingHistory: 'Tracking history',
  progressBar: 'Progress bar',
  orderDetails: 'Order details',
  review: 'Review',
  footer: 'Footer',
  productRecommend: 'Product recommendation',
};
export const SECTION_ORDER = Object.keys(SECTION_LABELS);

// Secondary descriptor shown under each field label in the Translation Manager (design parity).
export const SECTION_FIELD_DESCRIPTOR: Record<string, string> = {
  trackingStatus: 'Status label',
  trackingForm: 'Form text',
  trackingHistory: 'History label',
  progressBar: 'Progress label',
  orderDetails: 'Order detail',
  review: 'Review text',
  footer: 'Footer link',
  productRecommend: 'Section title',
};

// Field keys come in mixed styles (snake_case `plh_order_id`, camelCase `returnPage`). Render them as
// readable Title Case labels: split camelCase, expand known abbreviations, never show a raw abbreviation.
const KEY_ABBREVIATIONS: Record<string, string> = { plh: 'Placeholder', id: 'ID' };
export function humanizeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => KEY_ABBREVIATIONS[w.toLowerCase()] || w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Long-text fields render as <textarea> instead of <input>.
export const MULTILINE_FIELDS = new Set(['order_description', 'order_not_found_message', 'review_title']);

// Whether the merchant can add another language, plus the card subtitle and the lock-tooltip reason.
// Driven by `planLimit` — the max NON-DEFAULT languages the plan allows (backend tiers: Free=0,
// Launch=2, Scale=5, Pro=-1/unlimited) — and how many non-default languages already exist.
export interface AddLanguageQuota {
  canAdd: boolean;
  subtitle: string;
  lockReason?: string; // shown in the Add button tooltip only when locked
  banner?: { title: string; body: string }; // informational upsell above the card, only when gated
}

// The next tier to upgrade to, keyed by the current plan's additional-language limit.
const UPGRADE_REASON_BY_LIMIT: Record<number, string> = {
  0: 'Upgrade to the Launch plan or higher to add more languages.',
  2: 'Upgrade to the Scale plan or higher to add more languages.',
  5: 'Upgrade to the Pro plan for unlimited languages.',
};

// At-limit banner body, keyed by the plan's additional-language limit (2 = Launch, 5 = Scale).
const AT_LIMIT_BANNER_BODY: Record<number, string> = {
  2: "You've added the max number of languages on the Launch plan. Upgrade to add more.",
  5: "You've added the max number of languages on the Scale plan. Upgrade to Pro for unlimited languages.",
};

export function getAddLanguageQuota(list: ILanguageList): AddLanguageQuota {
  const total = list.languages.length;
  // Active = live on the storefront (the default language plus any published one).
  const activeCount = list.languages.filter(
    (l) => l.status === LanguageStatus.Default || l.status === LanguageStatus.Published,
  ).length;
  const limit = list.planLimit;
  // The plan caps PUBLISHED non-default languages (`used`) — the same metric as the publish gate.
  const used = list.used;
  const word = (n: number) => (n === 1 ? 'language' : 'languages');
  // How many languages are active (default + published) out of the total added.
  const subtitle = `${activeCount} of ${total} ${word(total)} active.`;

  // Pro — unlimited
  if (limit === -1) {
    return { canAdd: true, subtitle };
  }
  // Free — no additional quota
  if (limit === 0) {
    return {
      canAdd: false,
      subtitle,
      lockReason: UPGRADE_REASON_BY_LIMIT[0],
      banner: {
        title: 'Upgrade to add more languages',
        body: "Translate your tracking page into your customers' language. Available on the Launch plan and above.",
      },
    };
  }
  // Launch / Scale — capped by published languages
  const atLimit = used >= limit;
  return {
    canAdd: !atLimit,
    subtitle,
    lockReason: atLimit ? UPGRADE_REASON_BY_LIMIT[limit] : undefined,
    banner: atLimit ? { title: "You've reached your language limit", body: AT_LIMIT_BANNER_BODY[limit] } : undefined,
  };
}
